/**
 * useAspectLockedSize -- a decoration size (inches) locked to the artwork's
 * shape and kept inside a location's envelope.
 *
 * Jake, 2026-10-27: width and height are editable, with the aspect lock
 * "always on, shown as a visible lock icon with no checkbox". Changing one
 * side recomputes the other from the artwork's shape, clamped to the
 * location's envelope. Every size this produces is rounded to the cent
 * (``decorations.width_in`` is Numeric(5, 2)) and passes the backend's
 * same-shape rule (``services/decoration_size_validity``: 2% plus half a
 * cent of rounding slack per side; its 422 is ``size_aspect_mismatch``).
 *
 * Used by the product editor's placement inspector, the Add placement
 * wizard, and meant for any other surface that sizes a decoration (e.g.
 * digitizing a decoration that has no placement yet).
 *
 * API
 * ===
 * Pure helpers (no Vue):
 *
 *   fitToEnvelope(aspect, maxWidth, maxHeight) -> { width, height }
 *       The largest box of ``aspect`` (width / height) inside the envelope;
 *       the bare envelope when ``aspect`` is unknown (null). Mirrors
 *       ``ArtworkSizing.default_size`` on the backend.
 *   sizeFromWidth(width, { aspect, maxWidth, maxHeight, clamp = true, other })
 *   sizeFromHeight(height, { ... })                -> { width, height } | null
 *       The size with that side, the other side following the shape. With
 *       ``clamp`` (default) the result is the largest size of the shape not
 *       exceeding the requested side or the envelope. With an unknown shape
 *       the other side is ``other`` (only clamped). null for a value that is
 *       not a positive number.
 *   aspectMatches(width, height, aspect) -> boolean
 *       The validator's rule: |(w / h) / aspect - 1| <= 2% + 0.005 / w +
 *       0.005 / h. true when ``aspect`` is unknown (the server fails open
 *       on an unstamped file too).
 *   ASPECT_TOLERANCE = 0.02
 *
 * Composable:
 *
 *   const s = useAspectLockedSize({ aspect, maxWidth, maxHeight, fixed })
 *
 *   Each option may be a ref, a getter or a plain value. ``fixed`` (a stitch
 *   file: sewn at its own size) makes every setter a no-op.
 *
 *   s.width, s.height     refs (numbers, or null when unset)
 *   s.setWidth(v, { clamp = true })   width drives height
 *   s.setHeight(v, { clamp = true })  height drives width
 *   s.setSize(w, h)       set both as given (e.g. from the server), no lock
 *   s.fit()               the largest size of the shape in the envelope
 *   s.locked              computed: the lock applies (shape known, not fixed)
 *   s.withinEnvelope      computed: both sides inside the envelope
 *   s.shapeOk             computed: aspectMatches holds
 *   s.valid               computed: positive, within the envelope, shapeOk
 */
import { computed, ref, toValue } from "vue";

export const ASPECT_TOLERANCE = 0.02;

// decoration_size_validity._HALF_STEP_IN: a stored 2-decimal size is at most
// half a cent from the number meant, so each side earns that much slack.
const HALF_STEP_IN = 0.005;

// Half a cent of slack, as the backend allows a typed size onto the envelope.
const FIT_SLACK = 0.005;


function isPositive(n) {
    return typeof n === "number" && Number.isFinite(n) && n > 0;
}

function cents(n) {
    return Math.max(Math.round(n * 100) / 100, 0.01);
}

// Round DOWN to the cent: a side computed to sit at an envelope edge must not
// round past it.
function centsDown(n) {
    return Math.max(Math.floor(n * 100 + 1e-9) / 100, 0.01);
}

function aspectOf(aspect) {
    return isPositive(aspect) ? aspect : null;
}

export function aspectMatches(width, height, aspect) {
    const a = aspectOf(aspect);
    if (a == null) return true;
    if (!isPositive(width) || !isPositive(height)) return false;
    const slack = HALF_STEP_IN / width + HALF_STEP_IN / height;
    return Math.abs(width / height / a - 1) <= ASPECT_TOLERANCE + slack;
}

export function fitToEnvelope(aspect, maxWidth, maxHeight) {
    const a = aspectOf(aspect);
    if (!isPositive(maxWidth) || !isPositive(maxHeight)) return null;
    if (a == null) return { width: cents(maxWidth), height: cents(maxHeight) };
    if (a >= maxWidth / maxHeight) {
        return { width: cents(maxWidth), height: centsDown(maxWidth / a) };
    }
    return { width: centsDown(maxHeight * a), height: cents(maxHeight) };
}

function locked(side, value, { aspect, maxWidth, maxHeight, clamp = true, other = null }) {
    if (!isPositive(value)) return null;
    const a = aspectOf(aspect);
    const maxW = isPositive(maxWidth) ? maxWidth : Infinity;
    const maxH = isPositive(maxHeight) ? maxHeight : Infinity;
    if (a == null) {
        // Shape unknown: the sides are independent; only clamp.
        const keep = isPositive(other) ? other : null;
        let width = side === "width" ? value : keep;
        let height = side === "height" ? value : keep;
        if (clamp && width != null) width = Math.min(width, maxW);
        if (clamp && height != null) height = Math.min(height, maxH);
        return {
            width: width == null ? null : cents(width),
            height: height == null ? null : cents(height),
        };
    }
    // The driving side: as typed, or the largest the envelope allows for
    // this shape. The other side follows it, never rounding past its edge.
    const byWidth = side === "width";
    let drive = value;
    if (clamp) {
        drive = byWidth
            ? Math.min(value, maxW, maxH * a)
            : Math.min(value, maxH, maxW / a);
    }
    drive = cents(drive);
    const follow = byWidth ? drive / a : drive * a;
    const followMax = byWidth ? maxH : maxW;
    const followed = clamp && cents(follow) > followMax ? centsDown(followMax) : cents(follow);
    return byWidth
        ? { width: drive, height: followed }
        : { width: followed, height: drive };
}

export function sizeFromWidth(width, opts = {}) {
    return locked("width", width, opts);
}

export function sizeFromHeight(height, opts = {}) {
    return locked("height", height, opts);
}

export function useAspectLockedSize({ aspect, maxWidth, maxHeight, fixed = false } = {}) {
    const width = ref(null);
    const height = ref(null);

    const opts = () => ({
        aspect: toValue(aspect),
        maxWidth: toValue(maxWidth),
        maxHeight: toValue(maxHeight),
    });
    const isFixed = () => Boolean(toValue(fixed));

    function apply(size) {
        if (!size) return;
        width.value = size.width;
        height.value = size.height;
    }

    function setWidth(value, { clamp = true } = {}) {
        if (isFixed()) return;
        apply(sizeFromWidth(value, { ...opts(), clamp, other: height.value }));
    }

    function setHeight(value, { clamp = true } = {}) {
        if (isFixed()) return;
        apply(sizeFromHeight(value, { ...opts(), clamp, other: width.value }));
    }

    function setSize(w, h) {
        width.value = isPositive(w) ? w : null;
        height.value = isPositive(h) ? h : null;
    }

    function fit() {
        if (isFixed()) return;
        const o = opts();
        apply(fitToEnvelope(o.aspect, o.maxWidth, o.maxHeight));
    }

    const isLocked = computed(() => aspectOf(toValue(aspect)) != null && !isFixed());

    const withinEnvelope = computed(() => {
        const o = opts();
        if (!isPositive(width.value) || !isPositive(height.value)) return false;
        const okW = !isPositive(o.maxWidth) || width.value <= o.maxWidth + FIT_SLACK;
        const okH = !isPositive(o.maxHeight) || height.value <= o.maxHeight + FIT_SLACK;
        return okW && okH;
    });

    const shapeOk = computed(() =>
        isFixed() || aspectMatches(width.value, height.value, toValue(aspect)),
    );

    const valid = computed(() => withinEnvelope.value && shapeOk.value);

    return {
        width,
        height,
        setWidth,
        setHeight,
        setSize,
        fit,
        locked: isLocked,
        withinEnvelope,
        shapeOk,
        valid,
    };
}
